import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, getFavoriteListingIds } from '../../ducks/user.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';

// ================ Async Thunks ================ //

const queryFavoriteListingsPayloadCreator = (
  { config },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  return dispatch(fetchCurrentUser())
    .then(() => {
      const { currentUser } = getState().user;
      const favoriteIds = getFavoriteListingIds(currentUser);

      if (favoriteIds.length === 0) {
        return { listingRefs: [] };
      }

      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config.layout.listingImage;
      const aspectRatio = aspectHeight / aspectWidth;

      return sdk.listings
        .query({
          ids: favoriteIds.slice(0, 100),
          include: ['author', 'images'],
          'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        })
        .then(response => {
          dispatch(addMarketplaceEntities(response));
          const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
          return { listingRefs };
        });
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryFavoriteListingsThunk = createAsyncThunk(
  'FavoritesPage/queryFavoriteListings',
  queryFavoriteListingsPayloadCreator
);

// ================ Slice ================ //

const initialState = {
  queryInProgress: false,
  queryFavoritesError: null,
  currentPageResultIds: [],
};

const favoritesPageSlice = createSlice({
  name: 'FavoritesPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryFavoriteListingsThunk.pending, state => {
        state.queryInProgress = true;
        state.queryFavoritesError = null;
      })
      .addCase(queryFavoriteListingsThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.currentPageResultIds = action.payload.listingRefs.map(ref => ref.id);
      })
      .addCase(queryFavoriteListingsThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryFavoritesError = action.payload;
      });
  },
});

export default favoritesPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  return dispatch(queryFavoriteListingsThunk({ config }));
};
